// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type {
  BankUploadSection,
  BankUploadStep,
} from '@client/banks/types/bank-upload';
import { useStepsData } from '@shared/ui-kit/hooks/use-steps';
import { useCallback } from 'react';

export const useBankUploadSectionMeta = () => {
  const { steps, upsertMeta } = useStepsData();

  const isSectionCompleted = useCallback(
    (section: BankUploadSection): boolean =>
      Array.from(steps.values()).some(
        step => step.meta?.section === section && step.meta?.completed,
      ),
    [steps],
  );

  const markSectionCompleted = useCallback(
    (section: BankUploadSection) => {
      steps.forEach((step, id) => {
        if (step.meta?.section !== section) {
          return;
        }

        upsertMeta(id, { ...step.meta, completed: true });
      });
    },
    [steps, upsertMeta],
  );

  const isStepLocked = useCallback(
    (navStep: BankUploadStep): boolean => {
      const requires = steps.get(navStep)?.meta?.requires;

      if (!requires?.length) {
        return false;
      }

      return !requires.every((required: BankUploadSection) =>
        isSectionCompleted(required),
      );
    },
    [isSectionCompleted, steps],
  );

  return {
    isSectionCompleted,
    markSectionCompleted,
    isStepLocked,
  };
};
